import { SmartViewKind } from "@/domains/smart/constants";
import { logger } from "@/modules/logger";
import {
  MEM_COMMON_EDITOR_TRANSACTION_TRIGGER_METADATA_KEY,
  MemCommonEditorTransactionTrigger,
} from "@mem-labs/common-editor";
import { JsonValue } from "type-fest";
import type { AppStoreHeadsUpStore, SmartContextRequest } from "./AppStoreHeadsUpStore";

export interface HeadsUpEditorUpdatePayload {
  noteId: string;
  primaryLabel: string;
  isEmpty: boolean;
  transactionMetadata?: Record<string, JsonValue>;
}

export interface HeadsUpEditorBlurPayload {
  noteId: string;
  primaryLabel: string;
  isEmpty: boolean;
}

export interface HeadsUpTemplateInsertedPayload {
  noteId: string;
  primaryLabel: string;
  isEmpty: boolean;
  templateId?: string;
}

interface NoteAutoGenerationState {
  contextId: string;
  hasRemoteCopy: boolean;
  hasPrimaryLabel: boolean;
  hasAttempted: boolean;
  isDeferred: boolean;
  isReady: boolean;
}

export class HeadsUpAutoGenerator {
  private readonly stateByNoteId = new Map<string, NoteAutoGenerationState>();

  constructor(private readonly store: AppStoreHeadsUpStore) {}

  deferAutoGenerationFor(request: SmartContextRequest): void {
    const contextId = this.store.resolveContextIdForRequest(request);
    const noteId = request.activeNoteId ?? null;
    if (!contextId || !noteId) return;

    const existing = this.stateByNoteId.get(noteId);
    if (existing) {
      existing.contextId = contextId;
      existing.hasRemoteCopy = false;
      existing.hasPrimaryLabel = false;
      existing.hasAttempted = false;
      existing.isDeferred = true;
      existing.isReady = false;
      this.stateByNoteId.set(noteId, existing);
    } else {
      const state: NoteAutoGenerationState = {
        contextId,
        hasRemoteCopy: false,
        hasPrimaryLabel: false,
        hasAttempted: false,
        isDeferred: true,
        isReady: false,
      };

      this.stateByNoteId.set(noteId, state);
    }
    this.store.setAutoGenerationDeferredState(contextId, true);
  }

  markNoteSynced(request: SmartContextRequest): void {
    this.withState(request, state => {
      if (state.hasRemoteCopy) return;
      state.hasRemoteCopy = true;
      this.releaseIfReady(state);
    });
  }

  markNoteHasPrimaryLabel(request: SmartContextRequest): void {
    this.withState(request, state => {
      if (state.hasPrimaryLabel) return;
      state.hasPrimaryLabel = true;
      this.releaseIfReady(state);
    });
  }

  noteHasHeadsUp(request: SmartContextRequest): void {
    this.withState(request, state => {
      state.hasAttempted = true;
      state.isDeferred = false;
      state.isReady = true;
      this.store.setAutoGenerationDeferredState(state.contextId, false);
    });
  }

  handleEditorUpdate(payload: HeadsUpEditorUpdatePayload): void {
    const { noteId, primaryLabel, isEmpty, transactionMetadata } = payload;
    if (!noteId) return;

    const state = this.stateByNoteId.get(noteId);
    if (!state || !state.isDeferred) return;

    if (!state.isReady) return;
    if (!primaryLabel.trim()) return;
    if (isEmpty) return;
    if (state.hasAttempted) return;

    if (!this.transactionIndicatesEnter(transactionMetadata)) return;

    this.triggerGeneration(noteId, state);
  }

  handleEditorBlur(payload: HeadsUpEditorBlurPayload): void {
    const { noteId, primaryLabel, isEmpty } = payload;
    if (!noteId) return;

    const state = this.stateByNoteId.get(noteId);
    if (!state || !state.isDeferred) return;

    if (!state.isReady) return;
    if (state.hasAttempted) return;
    if (!primaryLabel.trim()) return;
    if (isEmpty) return;

    this.triggerGeneration(noteId, state);
  }

  handleTemplateInserted(payload: HeadsUpTemplateInsertedPayload): void {
    const { noteId } = payload;
    if (!noteId) return;

    this.withState(
      {
        activeNoteId: noteId,
        smartViewKind: SmartViewKind.HeadsUp,
      },
      state => {
        if (state.hasAttempted) return;
        this.triggerGeneration(noteId, state);
      }
    );
  }

  private triggerGeneration(noteId: string, state: NoteAutoGenerationState): void {
    state.hasAttempted = true;
    state.isDeferred = false;
    state.isReady = true;
    this.store.setAutoGenerationDeferredState(state.contextId, false);

    void this.store
      .generateForContext({
        activeNoteId: noteId,
        smartViewKind: SmartViewKind.HeadsUp,
      })
      .catch(error => {
        logger.warn({
          message: "[HeadsUpAutoGenerator] Auto generation attempt failed",
          info: {
            noteId,
            error: error instanceof Error ? error.message : "Unknown error",
          },
        });
      });
  }

  private releaseIfReady(state: NoteAutoGenerationState): void {
    if (!state.hasRemoteCopy || !state.hasPrimaryLabel) return;
    state.isReady = true;
  }

  private withState(
    request: SmartContextRequest,
    mutator: (state: NoteAutoGenerationState) => void
  ): void {
    const noteId = request.activeNoteId ?? null;
    if (!noteId) return;

    const contextId = this.store.resolveContextIdForRequest(request);
    if (!contextId) return;

    const state = this.stateByNoteId.get(noteId);
    if (!state) {
      const nextState: NoteAutoGenerationState = {
        contextId,
        hasRemoteCopy: false,
        hasPrimaryLabel: false,
        hasAttempted: false,
        isDeferred: false,
        isReady: false,
      };
      this.stateByNoteId.set(noteId, nextState);
      mutator(nextState);
      return;
    }

    if (state.contextId !== contextId) {
      state.contextId = contextId;
    }

    mutator(state);
  }

  private transactionIndicatesEnter(transactionMetadata?: Record<string, JsonValue>): boolean {
    if (!transactionMetadata) return false;
    const trigger = transactionMetadata[MEM_COMMON_EDITOR_TRANSACTION_TRIGGER_METADATA_KEY];
    return trigger === MemCommonEditorTransactionTrigger.Enter;
  }
}
